import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { RestaurantsPanel } from '../components/admin/RestaurantsPanel';
import { KnowledgePanel } from '../components/admin/KnowledgePanel';

const API = process.env.REACT_APP_BACKEND_URL;
const http = axios.create({ baseURL: API, withCredentials: true });

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState(null);
  const [leadMonth, setLeadMonth] = useState('all');
  const [notifyCfg, setNotifyCfg] = useState(null);
  const [notifyTest, setNotifyTest] = useState(null);
  const [resending, setResending] = useState('');
  const [showTestLeads, setShowTestLeads] = useState(true);
  const toggleTestLead = async (l) => {
    try {
      await http.post('/api/analytics/admin/leads/mark-test', { ...(l.session_id ? { session_id: l.session_id } : { callback_id: l.callback_id }), is_test: !l.is_test });
      setLeads(ls => ls.map(x => ((x.session_id || x.callback_id) === (l.session_id || l.callback_id) ? { ...x, is_test: !l.is_test } : x)));
    } catch {}
  };
  const resendLead = async (l) => {
    const key = l.session_id || l.callback_id;
    setResending(key);
    try { await http.post('/api/analytics/admin/leads/resend', l.session_id ? { session_id: l.session_id } : { callback_id: l.callback_id }); } catch {}
    await fetchLeads();
    setResending('');
  };
  const runNotifyTest = async () => {
    setNotifyTest({ pending: true });
    try { const r = await http.post('/api/analytics/admin/notify-test', {}); setNotifyTest(r.data); }
    catch { setNotifyTest({ email_sent: false, telegram_sent: false, email_error: 'request failed' }); }
  };
  const [loCfg, setLoCfg] = useState(null);
  const [loSaved, setLoSaved] = useState('');
  const loadLayoverCfg = () => http.get('/api/layover/config').then(r => setLoCfg(r.data)).catch(() => {});
  const setLo = (k) => (e) => setLoCfg(c => ({ ...c, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value }));
  const setTier = (dur, k) => (e) => setLoCfg(c => ({ ...c, tiers: { ...c.tiers, [dur]: { ...c.tiers[dur], [k]: e.target.value === '' ? null : Number(e.target.value) } } }));
  const setFee = (i, k) => (e) => setLoCfg(c => ({ ...c, entrance_fees: c.entrance_fees.map((f, j) => j === i ? { ...f, [k]: e.target.value } : f) }));
  const addFee = () => setLoCfg(c => ({ ...c, entrance_fees: [...c.entrance_fees, { site: '', site_ar: '', kind: 'single', amount: '', amount_evening: '' }] }));
  const removeFee = (i) => setLoCfg(c => ({ ...c, entrance_fees: c.entrance_fees.filter((_, j) => j !== i) }));
  const saveLayoverCfg = async () => {
    try { const r = await http.put('/api/layover/config', loCfg); setLoCfg(r.data); setLoSaved('Saved — live pages, calculator and chatbot use these values now. Static HTML for Google updates on the next rebuild + redeploy.'); }
    catch { setLoSaved('Save failed'); }
  };
  const [snap, setSnap] = useState(null);
  const loadSnapshotManifest = () => fetch(`${window.location.origin}/prerender-manifest.json?t=${Date.now()}`)
    .then(r => (r.ok ? r.json() : null)).then(d => setSnap(d && d.routes ? d : { unavailable: true })).catch(() => setSnap({ unavailable: true }));
  const snapAgeDays = snap?.generated_at ? Math.round((Date.now() - Date.parse(snap.generated_at)) / 86400000) : null;
  const [restUpdatedAt, setRestUpdatedAt] = useState(null);
  const lastConfigChange = [loCfg?.updated_at, restUpdatedAt].filter(Boolean).sort().pop();
  const snapConfigStale = !!(snap?.generated_at && lastConfigChange && Date.parse(lastConfigChange) > Date.parse(snap.config_updated_at || snap.generated_at));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchLeads = async () => {
    try {
      const res = await http.post('/api/analytics/admin/leads', {});
      setLeads(res.data.leads || []);
      setLeadStats(res.data.stats || null);
      setNotifyCfg(res.data.notify_config || null);
    } catch {
      setLeads([]);
      setLeadStats(null);
    }
  };

  const loadDashboard = async () => {
    const res = await http.post('/api/analytics/dashboard', {});
    setData(res.data);
    setAuthenticated(true);
    fetchLeads();
    loadLayoverCfg();
    loadSnapshotManifest();
  };

  useEffect(() => {
    http.get('/api/analytics/admin/me').then(() => loadDashboard()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await http.post('/api/analytics/admin/login', { password });
      setPassword('');
      await loadDashboard();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(err.response?.status === 429 ? (typeof d === 'string' ? d : 'Too many failed attempts. Try again in 15 minutes.') : 'Invalid password');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try { await http.post('/api/analytics/admin/logout', {}); } catch {}
    setAuthenticated(false);
    setData(null);
    setLeads([]);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await loadDashboard();
    } catch (err) {
      if (err.response?.status === 401) { setAuthenticated(false); setError('Session expired — please log in again'); }
      else setError('Failed to refresh');
    }
    setLoading(false);
  };

  const monthKey = (iso) => (iso ? iso.slice(0, 7) : '');
  const leadMonths = [...new Set(leads.map(l => monthKey(l.created_at)).filter(Boolean))].sort().reverse();
  const monthLeads = leadMonth === 'all' ? leads : leads.filter(l => monthKey(l.created_at) === leadMonth);
  const visibleLeads = monthLeads.filter(l => !l.is_test || showTestLeads);
  const countedLeads = monthLeads.filter(l => !l.is_test);
  const testCount = monthLeads.filter(l => l.is_test).length;
  const visibleStats = {
    tours: countedLeads.filter(l => l.path === 'tours').length,
    investment: countedLeads.filter(l => l.path === 'investment').length,
    unknown: countedLeads.filter(l => l.path !== 'tours' && l.path !== 'investment').length,
    chat: countedLeads.filter(l => l.handoff === 'chat').length,
    callback: countedLeads.filter(l => l.handoff === 'callback').length,
    undelivered: countedLeads.filter(l => l.notified !== true).length,
  };
  const deliveryCell = (l, i) => {
    const key = l.session_id || l.callback_id;
    const chips = [];
    if (l.email_sent) chips.push(<span key="e" className="admin-deliv-ok" data-testid={`chat-lead-email-ok-${i}`}>✅ Email</span>);
    if (l.telegram_sent) chips.push(<span key="t" className="admin-deliv-ok" data-testid={`chat-lead-telegram-ok-${i}`}>✅ Telegram</span>);
    const errs = [l.email_error && `Email: ${l.email_error}`, l.telegram_error && `Telegram: ${l.telegram_error}`].filter(Boolean).join('\n');
    return (
      <div className="admin-deliv" data-testid={`chat-lead-delivery-${i}`}>
        {l.notified ? chips : <span className="admin-deliv-fail" title={errs || 'No channel delivered'} data-testid={`chat-lead-not-delivered-${i}`}>⚠ NOT DELIVERED</span>}
        {l.notified && chips.length === 1 && errs && <span className="admin-deliv-warn" title={errs}>partial</span>}
        {l.notified && errs && <small className="admin-deliv-err" data-testid={`chat-lead-error-${i}`}>{errs.split('\n')[0].slice(0, 160)}</small>}
        {l.notify_attempts > 0 && <small>{l.notify_attempts} attempt{l.notify_attempts === 1 ? '' : 's'}</small>}
        {!l.notified && errs && <small className="admin-deliv-err">{errs.split('\n')[0].slice(0, 90)}</small>}
        {key && <button type="button" className="admin-link-btn" disabled={resending === key} onClick={() => resendLead(l)} data-testid={`chat-lead-resend-${i}`}>{resending === key ? 'Sending…' : (l.notified ? 'Send again' : 'Retry now')}</button>}
      </div>
    );
  };
  const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  if (!authenticated) {
    return (
      <>
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      <div className="admin-login-page" data-testid="admin-login-page">
        <form className="admin-login-form" onSubmit={handleLogin}>
          <h2>Analytics Dashboard</h2>
          <p>Enter admin password to continue</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
          />
          {error && <p className="admin-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn">
            {loading ? 'Loading...' : 'Login'}
          </button>
        </form>
      </div>
      </>
    );
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-header">
        <h1>Viaturkey Analytics</h1>
        <button onClick={refresh} disabled={loading} className="admin-refresh-btn" data-testid="admin-refresh-btn">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        <button onClick={handleLogout} className="admin-refresh-btn admin-logout-btn" data-testid="admin-logout-btn">Logout</button>
      </div>

      <div className="admin-stats-row">
        <div className="admin-stat-card" data-testid="stat-unique-visitors">
          <span className="admin-stat-number">{data.unique_visitors}</span>
          <span className="admin-stat-label">Unique Visitors</span>
        </div>
        <div className="admin-stat-card" data-testid="stat-total-views">
          <span className="admin-stat-number">{data.total_views}</span>
          <span className="admin-stat-label">Page Views</span>
        </div>
        <div className="admin-stat-card" data-testid="stat-total-clicks">
          <span className="admin-stat-number">{data.total_clicks}</span>
          <span className="admin-stat-label">Total Clicks</span>
        </div>
      </div>

      <div className="admin-grid">
        <div className="admin-panel">
          <h3>Page Views by Page</h3>
          <table className="admin-table">
            <thead><tr><th>Page</th><th>Views</th></tr></thead>
            <tbody>
              {data.page_views.map((p, i) => (
                <tr key={i}><td>{p.page}</td><td>{p.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-panel">
          <h3>Top Clicked Elements</h3>
          <table className="admin-table">
            <thead><tr><th>Element</th><th>Clicks</th></tr></thead>
            <tbody>
              {data.top_clicks.map((c, i) => (
                <tr key={i}><td>{c.element}</td><td>{c.count}</td></tr>
              ))}
              {data.top_clicks.length === 0 && <tr><td colSpan="2">No click data yet</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="admin-panel">
          <h3>Visitors by Language</h3>
          <table className="admin-table">
            <thead><tr><th>Language</th><th>Count</th></tr></thead>
            <tbody>
              {data.language_stats.map((l, i) => (
                <tr key={i}><td>{l.language?.toUpperCase()}</td><td>{l.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-panel">
          <h3>Daily Views (Last 7 Days)</h3>
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Views</th></tr></thead>
            <tbody>
              {data.daily_stats.map((d, i) => (
                <tr key={i}><td>{d.date}</td><td>{d.count}</td></tr>
              ))}
              {data.daily_stats.length === 0 && <tr><td colSpan="2">No data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-panel admin-full-width" data-testid="layover-config-panel">
        <h3>Layover Tours — operational config (single source of truth)</h3>
        {snap && (
          <div className={`admin-snap ${snap.unavailable ? '' : (snap.problem_count > 0 || snapConfigStale ? 'admin-snap-bad' : 'admin-snap-ok')}`} data-testid="snapshot-status">
            {snap.unavailable ? (
              <span>Static HTML status: not available in this environment (manifest is produced by the production build).</span>
            ) : (
              <>
                <strong>Static HTML for Google:</strong>
                <span>{snap.snapshots_ok}/{snap.sitemap_routes} sitemap routes prerendered · built {snap.generated_at ? `${new Date(snap.generated_at).toLocaleDateString('en-GB')} (${snapAgeDays} days ago)` : 'unknown'}{snap.api_origin ? ` · prices from ${snap.api_origin}` : ''}</span>
                {snap.problem_count > 0 && <span className="admin-deliv-fail" data-testid="snapshot-problems">⚠ {snap.problem_count} route problem(s): {Object.entries(snap.problems).filter(([, v]) => v.length).map(([k, v]) => `${k.replace(/_/g, ' ')} (${v.length})`).join(', ')}</span>}
                {snapConfigStale && <span className="admin-deliv-fail" data-testid="snapshot-config-stale">⚠ Layover config or restaurants were saved after the last build ({new Date(lastConfigChange).toLocaleString('en-GB')}) — Google still sees the old values until you rebuild and redeploy.</span>}
                {snap.problem_count === 0 && !snapConfigStale && <span className="admin-deliv-ok">✅ complete and current</span>}
                {snap.problems?.missing_snapshot?.length > 0 && <small>Missing: {snap.problems.missing_snapshot.join(', ')}</small>}
                {snap.problems?.stale_snapshot?.length > 0 && <small>Stale: {snap.problems.stale_snapshot.slice(0, 6).join(', ')}{snap.problems.stale_snapshot.length > 6 ? ` … +${snap.problems.stale_snapshot.length - 6}` : ''}</small>}
              </>
            )}
          </div>
        )}
        {loCfg && (
          <div className="admin-lo-grid">
            {[['arrival_min','Arrival: deplane + passport (min)'],['ist_oldcity_min','IST → Old City (min, each way)'],['saw_kadikoy_min','SAW → Kadıköy (min, each way)'],['saw_oldcity_min','SAW → Old City (min, each way)'],['return_buffer_min','Return buffer before departure (min)'],['min_layover_hours','Minimum layover (hours)'],['group_max','Max group size']].map(([k,l]) => (
              <label key={k}>{l}<input type="number" value={loCfg[k] ?? ''} onChange={setLo(k)} data-testid={`lo-cfg-${k}`} /></label>
            ))}
            <label>Transfer times status<input value={loCfg.transfer_times_status || ''} onChange={setLo('transfer_times_status')} /></label>
            <label>Entrance fees last checked<input value={loCfg.entrance_fees_checked || ''} onChange={setLo('entrance_fees_checked')} /></label>
            <label>Tier currency<input value={loCfg.tier_currency || ''} onChange={setLo('tier_currency')} /></label>
            {['7h','8h','10h'].map(d => ['p1_3','p4_6','p7_10'].map(k => (
              <label key={d+k}>{d} · {k.replace('p','').replace('_','–')} people<input type="number" placeholder="empty = shown as —" value={loCfg.tiers?.[d]?.[k] ?? ''} onChange={setTier(d,k)} data-testid={`lo-tier-${d}-${k}`} /></label>
            )))}
            <div className="admin-lo-fees" data-testid="lo-fees-editor">
              <h4>Entrance fees (amounts are language-neutral; Arabic site name entered once)</h4>
              <table className="admin-table">
                <thead><tr><th>Site (EN)</th><th>Site (AR)</th><th>Kind</th><th>Amount</th><th>Evening amount</th><th></th></tr></thead>
                <tbody>
                  {(loCfg.entrance_fees || []).map((f, i) => (
                    <tr key={i} data-testid={`lo-fee-row-${i}`}>
                      <td><input value={f.site} onChange={setFee(i, 'site')} data-testid={`lo-fee-site-${i}`} /></td>
                      <td><input dir="rtl" value={f.site_ar || ''} onChange={setFee(i, 'site_ar')} data-testid={`lo-fee-site-ar-${i}`} /></td>
                      <td><select value={f.kind} onChange={setFee(i, 'kind')} data-testid={`lo-fee-kind-${i}`}><option value="single">single</option><option value="day_evening">day / evening</option><option value="free">free</option></select></td>
                      <td><input value={f.amount || ''} disabled={f.kind === 'free'} placeholder="€25" onChange={setFee(i, 'amount')} data-testid={`lo-fee-amount-${i}`} /></td>
                      <td><input value={f.amount_evening || ''} disabled={f.kind !== 'day_evening'} placeholder="€50" onChange={setFee(i, 'amount_evening')} data-testid={`lo-fee-evening-${i}`} /></td>
                      <td><button type="button" className="admin-link-btn" onClick={() => removeFee(i)} data-testid={`lo-fee-remove-${i}`}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="admin-link-btn" onClick={addFee} data-testid="lo-fee-add">+ Add site</button>
            </div>
            <div className="admin-lo-actions">
              <button className="admin-refresh-btn" onClick={saveLayoverCfg} data-testid="lo-cfg-save">Save config</button>
              <span className="admin-lead-note">{loSaved} TA_URL_LAYOVER: {loCfg.ta_url_layover ? 'set (button visible)' : 'not set — Tripadvisor button hidden'}</span>
            </div>
          </div>
        )}
      </div>

      <KnowledgePanel http={http} />

      <RestaurantsPanel http={http} onMeta={setRestUpdatedAt} />

      <div className="admin-panel admin-full-width">
        <div className="admin-lead-head">
          <h3>Chatbot Leads ({countedLeads.length}{testCount ? ` + ${testCount} test` : ''}{leadMonth !== 'all' ? ` of ${leads.filter(l => !l.is_test).length}` : ''})</h3>
          <label className="admin-month-filter">
            <span>Month</span>
            <select value={leadMonth} onChange={(e) => setLeadMonth(e.target.value)} data-testid="lead-month-filter">
              <option value="all">All time</option>
              {leadMonths.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </label>
          <label className="admin-month-filter" data-testid="lead-show-test-toggle">
            <input type="checkbox" checked={showTestLeads} onChange={(e) => setShowTestLeads(e.target.checked)} data-testid="lead-show-test-checkbox" />
            <span>Show test leads ({testCount})</span>
          </label>
        </div>
        {leadStats && (
          <div className="admin-lead-stats" data-testid="lead-stats">
            <div className="admin-lead-stat admin-lead-stat-tours" data-testid="lead-stat-tours"><span>{visibleStats.tours}</span><small>Tours leads</small></div>
            <div className="admin-lead-stat admin-lead-stat-invest" data-testid="lead-stat-investment"><span>{visibleStats.investment}</span><small>Investment leads</small></div>
            <div className="admin-lead-stat" data-testid="lead-stat-unknown"><span>{visibleStats.unknown}</span><small>Path unknown</small></div>
            <div className="admin-lead-stat" data-testid="lead-stat-handoff-chat"><span>{visibleStats.chat}</span><small>Captured in chat</small></div>
            <div className="admin-lead-stat admin-lead-stat-handoff-callback" data-testid="lead-stat-handoff-callback"><span>{visibleStats.callback}</span><small>Callback form</small></div>
            <div className={`admin-lead-stat ${visibleStats.undelivered ? 'admin-lead-stat-fail' : ''}`} data-testid="lead-stat-undelivered"><span>{visibleStats.undelivered}</span><small>Not delivered</small></div>
          </div>
        )}
        {notifyCfg && (
          <div className="admin-notify-bar" data-testid="notify-config-bar">
            <span>Email: {notifyCfg.email_provider ? `${notifyCfg.email_provider} → ${notifyCfg.email_to}` : <strong className="admin-deliv-fail">not configured</strong>}</span>
            <span>Telegram: {notifyCfg.telegram_configured ? 'configured' : <strong className="admin-deliv-fail">not configured</strong>}</span>
            <button type="button" className="btn btn-secondary admin-small-btn" onClick={runNotifyTest} disabled={notifyTest?.pending} data-testid="notify-test-btn">{notifyTest?.pending ? 'Sending test…' : 'Send test notification'}</button>
            {notifyTest && !notifyTest.pending && (
              <span className="admin-notify-result" data-testid="notify-test-result">
                Email: {notifyTest.email_sent ? '✅ sent' : <span className="admin-deliv-fail">✗ {notifyTest.email_error}</span>} · Telegram: {notifyTest.telegram_sent ? '✅ sent' : <span className="admin-deliv-fail">✗ {notifyTest.telegram_error}</span>}
              </span>
            )}
          </div>
        )}
        <p className="admin-lead-note">WhatsApp and Calendly hand-offs leave the site, so they are counted in GA4 / PostHog (handoff_whatsapp, handoff_calendly) rather than here.</p>
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Path</th><th>Handoff</th><th>Name</th><th>Contact</th><th>Type</th><th>Request Summary</th><th>Delivery</th><th>Transcript</th></tr></thead>
            <tbody>
              {visibleLeads.map((l, i) => (
                <tr key={i} data-testid={`chat-lead-row-${i}`} className={`${l.notified ? '' : 'admin-row-undelivered'} ${l.is_test ? 'admin-row-test' : ''}`}>
                  <td>{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  <td><span className={`admin-path-pill admin-path-${l.path}`} data-testid={`chat-lead-path-${i}`}>{l.path}</span></td>
                  <td data-testid={`chat-lead-handoff-${i}`}>{l.handoff}</td>
                  <td>{l.name || '—'}{l.is_test && <span className="admin-test-chip" data-testid={`chat-lead-test-chip-${i}`}>TEST</span>}<br /><button type="button" className="admin-link-btn" onClick={() => toggleTestLead(l)} data-testid={`chat-lead-mark-test-${i}`}>{l.is_test ? 'Unmark test' : 'Mark as test'}</button></td>
                  <td>{l.contact}</td>
                  <td>{l.contact_type}</td>
                  <td style={{ maxWidth: '360px' }}>{l.request_summary}</td>
                  <td>{deliveryCell(l, i)}</td>
                  <td>
                    {l.transcript ? (
                      <details>
                        <summary style={{ cursor: 'pointer' }}>View</summary>
                        <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '240px', overflow: 'auto', fontSize: '0.75rem' }}>{l.transcript}</pre>
                      </details>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {visibleLeads.length === 0 && <tr><td colSpan="9">{leads.length === 0 ? 'No chatbot leads yet' : 'No leads in this month'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-panel admin-full-width">
        <h3>Recent Visitors (Last 50)</h3>
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead><tr><th>IP</th><th>Page</th><th>Language</th><th>Time</th><th>User Agent</th></tr></thead>
            <tbody>
              {data.recent_visitors.map((v, i) => (
                <tr key={i}>
                  <td>{v.ip}</td>
                  <td>{v.page}</td>
                  <td>{v.language?.toUpperCase()}</td>
                  <td>{new Date(v.timestamp).toLocaleString()}</td>
                  <td className="admin-ua-cell">{v.user_agent?.substring(0, 60)}...</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
